import React, { useCallback, useEffect, useRef, useState } from 'react';
import { pePortfolioService, PePortfolioMembershipDto, type PeAdminEmailPreview } from '@/services/pePortfolioService';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { useLanguage } from '@/state/language/LanguageProvider';

const panelClass = 'border border-[var(--color-border)] rounded-lg overflow-hidden';

/** Basic check: user likely finished local-part@domain.tld */
function looksLikeCompleteEmail(s: string): boolean {
  const trimmed = s.trim();
  const at = trimmed.indexOf('@');
  if (at < 1) return false;
  const domain = trimmed.slice(at + 1);
  return domain.includes('.') && domain.length >= 3;
}

/**
 * Portfolio company (child tenant) flow: request link to a PE firm, withdraw, cancel scheduled removal.
 * Used from /pe-portfolio (child tab) and Tenant configuration → Parent management.
 */
export const PePortfolioChildTenantPanel: React.FC = () => {
  const { t } = useLanguage();
  const [busy, setBusy] = useState(false);
  const [childMembership, setChildMembership] = useState<PePortfolioMembershipDto | null>(null);
  const [emailInput, setEmailInput] = useState('');
  const [pePreview, setPePreview] = useState<PeAdminEmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const previewSeq = useRef(0);

  const loadChild = useCallback(async () => {
    const m = await pePortfolioService.getCurrentMembership();
    setChildMembership(m);
  }, []);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadChild();
      } catch (e) {
        if (!cancelled) toast.error(pePortfolioService.errMessage(e));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loadChild]);

  useEffect(() => {
    const email = emailInput.trim();
    if (!looksLikeCompleteEmail(email)) {
      setPePreview(null);
      setPreviewLoading(false);
      return;
    }
    const seq = ++previewSeq.current;
    const timeoutId = window.setTimeout(() => {
      setPreviewLoading(true);
      void (async () => {
        try {
          const result = await pePortfolioService.previewPeAdminEmail(email);
          if (previewSeq.current !== seq) return;
          setPePreview(result);
        } catch {
          if (previewSeq.current !== seq) return;
          setPePreview(null);
        } finally {
          if (previewSeq.current === seq) setPreviewLoading(false);
        }
      })();
    }, 400);
    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [emailInput]);

  const onRequestLink = async () => {
    try {
      setBusy(true);
      const row = await pePortfolioService.requestLink(emailInput.trim());
      setChildMembership(row);
      setEmailInput('');
      toast.success(t('pePortfolio.toast.requestSent'));
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onWithdraw = async () => {
    try {
      setBusy(true);
      const row = await pePortfolioService.withdrawRequest();
      setChildMembership(row);
      toast.success(t('pePortfolio.toast.requestWithdrawn'));
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  const onReverseDeletion = async () => {
    try {
      setBusy(true);
      const row = await pePortfolioService.reverseScheduledDeletion();
      setChildMembership(row);
      toast.success(t('pePortfolio.toast.scheduledRemovalCancelledChild'));
    } catch (e) {
      toast.error(pePortfolioService.errMessage(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      {childMembership?.status === 'soft_delete_scheduled' && (
        <div
          className="border border-[var(--color-warning)] rounded-lg p-4 bg-[var(--color-secondary-bg)]"
          role="alert"
        >
          <p className="font-medium text-[var(--color-surface-text)]">
            {t('pePortfolio.child.scheduledRemoval.title')}
          </p>
          <p className="mt-1 text-sm text-[var(--color-secondary-text)]">
            {t('pePortfolio.child.scheduledRemoval.body', {
              date: childMembership.hardDeleteOn ?? '—',
            })}
          </p>
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={busy}
            className="mt-4"
            onClick={() => void onReverseDeletion()}
          >
            {t('pePortfolio.child.scheduledRemoval.cancelBtn')}
          </Button>
        </div>
      )}

      <div className={`${panelClass} p-4`}>
        <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
          {t('pePortfolio.child.linkSection.title')}
        </h2>
        <p className="mt-2 text-sm text-[var(--color-secondary-text)] leading-relaxed">
          {t('pePortfolio.child.linkSection.description')}
        </p>
        {childMembership && (
          <p className="mt-4 text-sm text-[var(--color-surface-text)]">
            <span className="text-[var(--color-secondary-text)]">{t('pePortfolio.child.status.currentStatus')} </span>
            <span className="font-medium">{childMembership.status.replace(/_/g, ' ')}</span>
            {childMembership.peAdminEmailSnapshot && (
              <span className="text-[var(--color-secondary-text)]"> — {childMembership.peAdminEmailSnapshot}</span>
            )}
          </p>
        )}
        <div className="mt-4 flex flex-col gap-3 sm:flex-row sm:items-end">
          <label className="flex-1 block min-w-0">
            <span className="text-xs font-medium uppercase tracking-wide text-[var(--color-secondary-text)]">
              {t('pePortfolio.child.email.label')}
            </span>
            <input
              type="email"
              value={emailInput}
              onChange={(ev) => setEmailInput(ev.target.value)}
              className="mt-1.5 w-full rounded-lg border border-[var(--color-border)] bg-[var(--color-primary-bg)] px-3 py-2.5 text-sm text-[var(--color-primary-text)] placeholder:text-[var(--color-secondary-text)]"
              placeholder={t('pePortfolio.child.email.placeholder')}
              disabled={busy}
              autoComplete="email"
            />
            <div className="mt-2 min-h-[1.25rem]" aria-live="polite">
              {previewLoading && looksLikeCompleteEmail(emailInput.trim()) && (
                <p className="text-xs text-[var(--color-secondary-text)]">{t('pePortfolio.child.lookup.loading')}</p>
              )}
              {!previewLoading && pePreview?.status === 'match' && (
                <p className="text-sm text-[var(--color-surface-text)]">
                  <span className="text-[var(--color-secondary-text)]">{t('pePortfolio.child.lookup.firmPrefix')} </span>
                  <span className="font-medium">{pePreview.peFirmName}</span>
                </p>
              )}
              {!previewLoading && pePreview?.status === 'ambiguous' && (
                <p className="text-xs text-[var(--color-warning)]">{t('pePortfolio.child.lookup.ambiguous')}</p>
              )}
              {!previewLoading && pePreview?.status === 'none' && looksLikeCompleteEmail(emailInput.trim()) && (
                <p className="text-xs text-[var(--color-secondary-text)]">{t('pePortfolio.child.lookup.none')}</p>
              )}
            </div>
          </label>
          <Button
            type="button"
            variant="primary"
            size="md"
            disabled={busy || !emailInput.trim()}
            onClick={() => void onRequestLink()}
          >
            {t('pePortfolio.child.sendRequest')}
          </Button>
        </div>
        {childMembership?.status === 'pending_pe_approval' && (
          <button
            type="button"
            disabled={busy}
            onClick={() => void onWithdraw()}
            className="mt-4 text-sm font-medium text-[var(--color-info)] underline underline-offset-2 disabled:opacity-50"
          >
            {t('pePortfolio.child.withdrawRequest')}
          </button>
        )}
      </div>
    </div>
  );
};
